import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { Plus, Edit2, Trash2, Menu, X, Save, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const AdminBookingTypes = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookingTypes, setBookingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_order: 0
  });

  useEffect(() => {
    const effectiveRole = user.effective_role || user.role;
  if (effectiveRole !== 'Admin' && effectiveRole !== 'Manager') {
      toast.error('Only Admin/Manager can access this page');
      navigate('/');
      return;
    }
    loadBookingTypes();
  }, [user, navigate]);

  const loadBookingTypes = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/booking-types/all');
      setBookingTypes(res.data);
    } catch (err) {
      console.error('Error loading booking types:', err);
      toast.error('Failed to load booking types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingType(null);
    setFormData({ name: '', display_order: bookingTypes.length + 1 });
    setShowModal(true);
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({ name: type.name, display_order: type.display_order });
    setShowModal(true);
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this booking type?')) return;
    
    try {
      await axiosInstance.delete(`/booking-types/${typeId}`);
      toast.success('Booking type deleted successfully');
      loadBookingTypes();
    } catch (err) {
      console.error('Error deleting booking type:', err);
      toast.error('Failed to delete booking type');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingType) {
        await axiosInstance.put(`/booking-types/${editingType.type_id}`, formData);
        toast.success('Booking type updated successfully');
      } else {
        await axiosInstance.post('/booking-types', formData);
        toast.success('Booking type created successfully');
      }
      setShowModal(false);
      loadBookingTypes();
    } catch (err) {
      console.error('Error saving booking type:', err);
      toast.error('Failed to save booking type');
    }
  };

  const initializeDefaults = async () => {
    if (!window.confirm('Initialize default booking types? This will only work if no types exist.')) return;
    
    try {
      const res = await axiosInstance.post('/booking-types/initialize');
      toast.success(res.data.message);
      loadBookingTypes();
    } catch (err) {
      console.error('Error initializing:', err);
      toast.error(err.response?.data?.detail || 'Failed to initialize');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="page-container">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <button 
              onClick={() => setMenuOpen(true)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="page-title">Booking Types</h1>
          </div>
        </div>

        <div className="mb-6 flex gap-3">
          <button
            onClick={handleAdd}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Booking Type
          </button>
          <button
            onClick={initializeDefaults}
            className="btn-secondary flex items-center gap-2"
          >
            Initialize Defaults
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
          </div>
        ) : bookingTypes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No booking types found</p>
            <button onClick={initializeDefaults} className="btn-primary">
              Initialize Default Types
            </button>
          </div>
        ) : (
          <div className="premium-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-amber-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-bold">Order</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookingTypes.map((type) => (
                  <tr key={type.type_id} className="border-t border-amber-100">
                    <td className="px-4 py-3 text-sm">{type.display_order}</td>
                    <td className="px-4 py-3 text-sm font-semibold">{type.name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        type.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {type.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleEdit(type)}
                        className="mr-2 p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(type.type_id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingType ? 'Edit' : 'Add'} Booking Type
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., Ornaments, Cadbury (995)"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  className="input-field w-full"
                  min="0"
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <Save className="w-4 h-4" />
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
    </div>
  );
};

export default AdminBookingTypes;
